import { useMemo, useContext } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import { PERMISSIONS } from "constants/portal";
import { allowAction, permissionChecker } from "helpers/common";

export const useCredentialPermissions = (credentialData, isEditor) => {
  const { user } = useAuth0();
  const { userPermissions } = useContext(PermissionsContext);

  const sharedUserData = useMemo(
    () => credentialData?.share_user?.find((sharedUser) => sharedUser.user_id === user?.cxfUserId),
    [credentialData, user]
  );

  const permissions = useMemo(() => {
    if (!user) {
      return {
        canOpen: false,
        canEdit: false,
        canShare: false,
        canDelete: false,
        canView: false,
        hasSuperUserAccess: false,
        isOwner: false,
        shouldHideMenu: false
      };
    }

    const isOwner = !credentialData || allowAction(user.cxfUserId, credentialData?.owner_id);
    const isShared = credentialData?.share_user?.some((u) => u.user_id === user.cxfUserId);
    const shareType = sharedUserData?.share_type;

    const hasSuperUserAccess = [
      PERMISSIONS.EDIT_ANY_CREDENTIALS,
      PERMISSIONS.DELETE_ANY_CREDENTIALS,
      PERMISSIONS.SHARE_ANY_CREDENTIALS,
      PERMISSIONS.VIEW_ANY_CREDENTIALS
    ].some((permission) => permissionChecker(userPermissions, permission));

    const hasUserPermissionAccess =
      [
        PERMISSIONS.MODIFY_CREDENTIALS,
        PERMISSIONS.DELETE_CREDENTIALS,
        PERMISSIONS.SHARE_CREDENTIALS,
        PERMISSIONS.CREATE_CREDENTIALS
      ].some((permission) => permissionChecker(userPermissions, permission)) ||
      shareType === "view";

    const canOpen =
      hasSuperUserAccess || isOwner || isEditor || (isShared && hasUserPermissionAccess);

    const canEdit =
      permissionChecker(userPermissions, PERMISSIONS.EDIT_ANY_CREDENTIALS) ||
      (isOwner && permissionChecker(userPermissions, PERMISSIONS.MODIFY_CREDENTIALS)) ||
      (permissionChecker(userPermissions, PERMISSIONS.MODIFY_CREDENTIALS) &&
        shareType === "edit") ||
      isEditor;

    const canShare =
      permissionChecker(userPermissions, PERMISSIONS.SHARE_ANY_CREDENTIALS) ||
      (isOwner && permissionChecker(userPermissions, PERMISSIONS.SHARE_CREDENTIALS)) ||
      isEditor;

    const canDelete =
      permissionChecker(userPermissions, PERMISSIONS.DELETE_ANY_CREDENTIALS) ||
      (isOwner && permissionChecker(userPermissions, PERMISSIONS.DELETE_CREDENTIALS)) ||
      isEditor;

    const shouldHideMenu =
      (shareType === "view" || shareType === "use") && !isEditor && !hasSuperUserAccess;

    return {
      canOpen,
      canEdit,
      canShare,
      canDelete,
      canView: true,
      hasSuperUserAccess,
      isOwner,
      shouldHideMenu
    };
  }, [user, userPermissions, credentialData, sharedUserData, isEditor]);

  return permissions;
};
